<template>
  <table class="table is-fullwidth">
    <thead>
      <tr>
        <th class="is-hidden-mobile">
          <a @click.prevent="emit('sort-click', 'name')">Ingredient</a>
        </th>
        <th>
          <div class="is-flex is-justify-content-space-between">
            <span class="is-hidden-mobile icon-text">
              <span class="mr-2">Show on Label as</span>
              <IconHint :content="ingredientListDualHint" :interactive="true" />
            </span>
            <div class="breadcrumb">
              <ul>
                <li class="pr-3" :class="{ 'is-active': language == 'english' }">
                  <a href="" class="pr-0" @click.prevent="language = 'english'">English</a>
                  <IconHint
                    class="is-hidden-tablet"
                    :content="ingredientListEnglishHint"
                    :interactive="true"
                  />
                </li>
                <li :class="{ 'is-active': language == 'french' }">
                  <a
                    id="toggle-french-ingredients"
                    href=""
                    class="pr-0"
                    @click.prevent="language = 'french'"
                  >
                    French
                  </a>
                  <IconHint
                    class="is-hidden-tablet"
                    :content="ingredientListFrenchHint"
                    :interactive="true"
                  />
                </li>
              </ul>
            </div>
          </div>
        </th>
        <th class="is-hidden-touch is-hidden-desktop-only">
          <span class="icon-text-inline">
            <span>Spice/flavor</span>
            <IconHint :content="spiceFlavorHint" :interactive="true" />
          </span>
        </th>
        <th></th>
      </tr>
    </thead>

    <tbody>
      <tr v-if="props.recipe?.subrecipe" :class="{ 'is-expanded': expandSubrecipe }">
        <td>
          <span>
            How to list subrecipe name in other
            <span class="icon-text">
              <span>recipes?</span>
              <IconHint :content="subrecipeIngredientMetaNameHint" />
            </span>
          </span>
          <a
            class="is-hidden-tablet"
            href=""
            data-expand-row
            @click.prevent="expandSubrecipe = !expandSubrecipe"
          >
            <span class="icon">
              <i class="fa fa-chevron-right"></i>
            </span>
          </a>
        </td>

        <td>
          <div v-show="language == 'english'" style="width: 100%">
            <GrowingTextarea
              data-test-id="subrecipe_name_edit"
              :value="props.recipe.subrecipe_name"
              :disabled="props.recipe && !props.recipe.editable"
              @change="updateRecipe('subrecipe_name', $event)"
            />
          </div>

          <div v-show="language == 'french'" style="width: 100%">
            <GrowingTextarea
              :value="props.recipe.subrecipe_name_french"
              :disabled="props.recipe && !props.recipe.editable"
              @change="updateRecipe('subrecipe_name_french', $event)"
            />
          </div>

          <!-- just for spacing -->
          <a class="is-invisible" href="" data-expand-row>
            <span class="icon">
              <i class="fa fa-chevron-right"></i>
            </span>
          </a>
        </td>

        <!-- Spice/flavor, other columns n/a for subrecipes -->
        <td></td>
        <td></td>
      </tr>

      <tr
        v-for="(im, index) in ingredientMetaStore.ingredientMetas"
        :id="`im-row-${im.id}`"
        :key="im.id"
        :class="{ 'is-expanded': expanded[index] }"
      >
        <td>
          <div class="is-flex is-align-items-center gap-2 is-flex-wrap-wrap">
            <a
              :href="
                im.ingredient.subrecipe
                  ? ingredientMetasRecipePath(im.ingredient.recipe_id)
                  : ingredientPath(im.ingredient.id, { user_id: userId })
              "
              target="blank"
              tabindex="-1"
            >
              {{ im.ingredient.name }}
            </a>

            <div v-if="im.ingredient.subrecipe" class="is-flex is-align-items-center gap-1">
              <span class="item-chip subrecipe-chip ml-1">Subrecipe</span>
              <IconHint :content="subrecipeHint" />
            </div>

            <IngredientTagInput
              v-if="props.showTags"
              :user-id="userId"
              :ingredient-id="im.ingredient.id"
              :tags="im.ingredient.tags"
              :can-click="true"
              empty-caption=""
              @tag-click="(tag) => emit('tag-click', tag)"
            />
          </div>

          <a
            href=""
            class="is-hidden-tablet"
            data-expand-row
            @click.prevent="expanded[index] = !expanded[index]"
          >
            <span class="icon">
              <i class="fa fa-chevron-right"></i>
            </span>
          </a>
        </td>

        <td>
          <div v-show="language == 'english'" style="width: 100%">
            <label class="label is-hidden-tablet" :for="`english-ingredient-list-${im.id}`">
              Show on Label as
            </label>
            <GrowingTextarea
              :id="`english-ingredient-list-${im.id}`"
              :value="im.ingredient_list"
              name="ingredient_meta[ingredient_list]"
              :disabled="isDisabled(props.recipe)"
              @change="updateIngredientMeta(im, 'ingredient_list', $event)"
            />
          </div>

          <div v-show="language == 'french'" style="width: 100%">
            <label class="label is-hidden-tablet" :for="`french-ingredient-list-${im.id}`">
              Show on Label as
            </label>
            <GrowingTextarea
              :id="`french-ingredient-list-${im.id}`"
              :value="im.new_canada_french_ingredient_list"
              name="ingredient_meta[new_canada_french_ingredient_list]"
              :disabled="props.recipe && !props.recipe.editable"
              @change="updateIngredientMeta(im, 'new_canada_french_ingredient_list', $event)"
            />
            <a
              v-if="
                (!props.recipe || props.recipe.editable) && im.ingredient_list.trim().length > 0
              "
              href=""
              class="button is-small is-info is-outlined mt-2"
              @click.prevent="translateIngredientMeta(im)"
            >
              Translate
            </a>
          </div>

          <a
            v-if="!im.ingredient.subrecipe"
            href=""
            class="is-hidden-mobile is-hidden-widescreen no-print"
            tabIndex="-1"
            data-expand-row
            @click.prevent="expanded[index] = !expanded[index]"
          >
            <span class="icon">
              <i class="fa fa-chevron-right"></i>
            </span>
          </a>
        </td>

        <td>
          <div v-if="!im.ingredient.subrecipe" class="field">
            <label class="label is-hidden-widescreen no-print" :for="`spice-flavor-${im.id}`">
              <span class="icon-text-inline">
                <span>Spice/flavor</span>
                <IconHint :content="spiceFlavorHint" :interactive="true" />
              </span>
            </label>
            <div class="control">
              <div class="select">
                <select
                  :id="`spice-flavor-${im.id}`"
                  :value="im.spice_flavor"
                  :disabled="props.recipe && !props.recipe.editable"
                  @change="updateIngredientMeta(im, 'spice_flavor', $event.target.value)"
                >
                  <option value="">
                    {{ im.spice_flavor == "" ? "" : "Not Spice/Flavor" }}
                  </option>
                  <option>Spice</option>
                  <option>Flavor</option>
                  <option>Natural Flavor</option>
                  <option>Artificial Flavor</option>
                  <option>Artificial Color</option>
                  <option value="Spice And Coloring">Spice & Coloring</option>
                </select>
              </div>
            </div>
          </div>
        </td>

        <td>
          <div class="is-flex is-flex-direction-column">
            <div v-if="!im.ingredient.subrecipe" class="is-flex is-justify-content-space-between">
              <label class="checkbox" :for="`canada-sugar-${im.id}`">
                <input
                  :id="`canada-sugar-${im.id}`"
                  type="checkbox"
                  :checked="im.canada_sugar"
                  :disabled="props.recipe && !props.recipe.editable"
                  @change="updateIngredientMeta(im, 'canada_sugar', $event.target.checked)"
                />
                <span class="icon-text-inline ml-1">
                  <span>Canada sugar</span>
                </span>
              </label>
              <IconHint class="ml-1" :content="canadaSugarHint" :interactive="true" />
            </div>
            <div
              class="is-flex is-justify-content-space-between"
              :class="{ disabled: isDisabled(props.recipe) }"
            >
              <label class="checkbox" :for="`bioengineered-${im.id}`">
                <input
                  :id="`bioengineered-${im.id}`"
                  type="checkbox"
                  :checked="im.bioengineered"
                  :disabled="isDisabled(props.recipe)"
                  @change="updateIngredientMeta(im, 'bioengineered', $event.target.checked)"
                />
                <span class="icon-text-inline ml-1">
                  <span>Bioengineered</span>
                </span>
              </label>
              <IconHint class="ml-1" :content="bioengineeredHint" :interactive="true" />
            </div>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>import { ref as _ref } from 'vue';

  import { onBeforeMount } from "vue";

  import IngredientTagInput from "./ingredient-tag-input.vue";
  import GrowingTextarea from "./growing-textarea.vue";
  import IconHint from "./icon-hint.vue";

  import { useIngredientMetaStore } from "./stores/ingredient-meta-store.js";

  import {
    ingredientListDualHint,
    ingredientListEnglishHint,
    ingredientListFrenchHint,
    spiceFlavorHint,
    canadaSugarHint,
    bioengineeredHint,
    subrecipeIngredientMetaNameHint,
  } from "./lib/constants";

  import { ingredientPath, updateSubrecipeNameRecipePath, ingredientMetasRecipePath } from "routes";

  const props = defineProps({
    userId: { type: String, required: true },
    showTags: { type: Boolean, default: true },

    preload: { type: Object, required: false, default: undefined },

    /* Used for ingredient_metas#index_for_recipe */
    recipe: { type: Object, require: false, default: undefined },
  });

  const ingredientMetaStore = useIngredientMetaStore();

  const updateIngredientMeta = async (ingredientMeta, field, value) => {
    const result = await ingredientMetaStore.updateIngredientMeta(
      ingredientMeta,
      field,
      value,
      props.recipe?.id
    );

    if (!result) {
      window.Flash.error("Ingredient list failed to update");
    }
  };

  const subrecipeHint =
    "You can update the subrecipe ingredient statement on their own recipe page.";

  const isDisabled = (recipe) => recipe && !recipe.editable;

  const translateIngredientMeta = async (ingredientMeta) => {
    const translation = await ingredientMetaStore.translateIngredientMeta(ingredientMeta);
    if (translation) {
      updateIngredientMeta(ingredientMeta, "new_canada_french_ingredient_list", translation);
    } else {
      window.Flash.error("Failed to translate");
    }
  };

  const updateRecipe = async (field, value) => {
    try {
      const response = await fetch(updateSubrecipeNameRecipePath(props.recipe.id), {
        method: "POST",
        headers: { "Accept": "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ recipe: { [field]: value } }),
      });

      if (response.ok) {
        window.Flash.success("Ingredient list updated");
      } else {
        window.Flash.error("Ingredient list failed to update");
      }
    } catch (_error) {
      window.Flash.error("Ingredient list failed to update");
    }
  };

  const language = _ref("english");
  const expanded = _ref(new Array(100).fill(false));
  const expandSubrecipe = _ref(false);
  const emit = defineEmits(["sort-click", "tag-click"]);

  onBeforeMount(() => {
    ingredientMetaStore.assignInitial({ userId: props.userId }, { patch: true });
    if (props.preload) {
      ingredientMetaStore.$patch({ loaded: true, ...props.preload });
    }
  });
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  table {
    $name-column: 1;
    $ingredient-list-column: 2;
    $spice-flavor-column: 3;
    $other-column: 4;

    th {
      vertical-align: bottom;
    }

    .disabled {
      opacity: 0.5;
      .checkbox {
        cursor: not-allowed;
      }
    }

    [data-expand-row] {
      display: inline-block;
      height: 100%;
      padding-left: 1rem;
      text-align: right;
    }

    @include widescreen {
      tbody > tr {
        td:nth-of-type(#{$name-column}) {
          min-width: 200px;
        }

        td:nth-of-type(#{$ingredient-list-column}) {
          min-width: 340px;
        }

        td:nth-of-type(#{$spice-flavor-column}) {
          padding-left: 0;
          padding-right: 0;

          div.select {
            width: 15ch !important;
          }
        }

        td:nth-of-type(#{$other-column}) {
          div {
            text-wrap: nowrap;
            margin-bottom: 0.25rem;
          }
        }
      }
    }

    @media screen and (width < $widescreen) {
      & > thead {
        & > tr {
          display: flex;

          & > th {
            border: none;
          }

          & > th:nth-of-type(#{$name-column}) {
            flex-basis: 35%;
          }
          & > th:nth-of-type(#{$ingredient-list-column}) {
            flex-grow: 1;
          }
        }
      }
      & > tbody {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        & > tr {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          justify-content: flex-end;

          border: 1px solid $grey-light;
          border-radius: $radius-large;

          & > td {
            border: none;
          }

          td:nth-of-type(#{$name-column}) {
            flex-basis: 35%;
          }
          td:nth-of-type(#{$ingredient-list-column}) {
            flex-basis: 65%;
            display: flex;
            justify-content: space-between;

            form {
              width: 100%;
            }
          }
          td:nth-of-type(#{$spice-flavor-column}) {
            display: none;
          }
          td:nth-of-type(#{$other-column}) {
            display: none;
          }

          &.is-expanded {
            [data-expand-row] {
              .icon {
                transform: rotate(90deg);
              }
            }

            & > td:nth-of-type(#{$spice-flavor-column}) {
              display: flex;
              flex-basis: 65%;
            }
            & > td:nth-of-type(#{$other-column}) {
              flex-basis: 65%;
              display: flex;
              align-items: flex-end;
              margin-bottom: 0.5rem;
            }
          }
        }
      }
    }

    @include mobile {
      tbody > tr {
        justify-content: flex-start;

        td:nth-of-type(#{$name-column}) {
          display: flex;
          flex-basis: 100%;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;

          [data-expand-row] {
            flex-grow: 1;
          }
        }

        td:nth-of-type(#{$ingredient-list-column}) {
          flex-basis: 100%;
        }
        td:nth-of-type(#{$spice-flavor-column}) {
          flex-basis: 75% !important;
        }
        td:nth-of-type(#{$other-column}) {
          flex-basis: 75% !important;
        }

        &:not(.is-expanded) {
          td:nth-of-type(#{$ingredient-list-column}) {
            display: none;
          }
        }
      }
    }
  }
</style>
